import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, CheckCircle2, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './Home.css';

const Home = () => {
  const navigate = useNavigate();
  const whatsappNumber = '5493413047536';
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=\u00a1Hola!`;

  const representaciones = [
    {
      id: 1,
      logo: '/assets/logos/KSB-LOGO.png',
      title: 'KSB',
      description: 'Líder en sistemas de bombeo y válvulas industriales. Reconocida por su confiabilidad, innovación y rendimiento.',
      link: '/ksb'
    },
    {
      id: 2,
      logo: '/assets/logos/TECNAL-LOGO.png',
      title: 'TECNAL',
      description: 'Soluciones industriales para plantas aceiteras, bioetanol, alimentación animal, almacenamiento de granos y movimiento portuario.',
      link: '/representaciones/tecnal'
    }
  ];

  const productosKSB = [
    {
      id: 1,
      title: 'Bombas centrífugas y de proceso',
      description: 'Equipos para impulsión, transporte y manejo de fluidos industriales con máxima fiabilidad y eficiencia energética.',
      link: '/ksb/bombas'
    },
    {
      id: 2,
      title: 'Válvulas industriales',
      description: 'Control seguro y eficiente para líneas de producción y sistemas de alta presión, diseñadas para larga vida útil y mínimo mantenimiento.',
      link: '/ksb/valvulas'
    },
    {
      id: 3,
      title: 'Sistemas de presurización (Hyamat)',
      description: 'Soluciones automáticas para mantener presión constante en redes de distribución de agua y sistemas contra incendios. Diseñadas para operación silenciosa, ahorro energético y monitoreo remoto.',
      link: '/assets/ksb/KSB Hyamat - Sistemas de Presurización.pdf',
      isPdf: true
    },
    {
      id: 4,
      title: 'Monitoreo inteligente (KSB Guard)',
      description: 'Sistema de sensores conectados que permite vigilar en tiempo real el estado de bombas y equipos. Ayuda a prevenir fallas, reducir costos de mantenimiento y optimizar la disponibilidad operativa.',
      link: '/assets/ksb/KSB Guard - Monitoreo.pdf',
      isPdf: true
    }
  ];

  const equiposTecnal = [
    {
      id: 1,
      title: 'Expander EAP / EXP',
      description: 'Equipos de alta capacidad para la preparación y expansión de granos oleaginosos, alcanzando procesamientos de hasta 25 toneladas por hora.'
    },
    {
      id: 2,
      title: 'Secador Horizontal',
      description: 'Secadores de última generación con sistema de alimentación continua que garantiza homogeneidad térmica y mayor aprovechamiento energético. Ideales para planta de etanol, balanceado o extrusado de alimentos.'
    },
    {
      id: 3,
      title: 'Extrusora EXP / EAP',
      description: 'Desarrolladas para procesos de extrusión continua de soja y maíz, combinan alta eficiencia mecánica con mantenimiento simple y bajo consumo. Perfectas para plantas de aceite vegetal y alimentación animal.'
    },
    {
      id: 4,
      title: 'Mezcladora / Recubridor TMP',
      description: 'Sistema dual de mezclado y recubrimiento simultáneo, optimiza la homogeneidad del producto final. Aplicable en líneas de balanceado animal y extrusado.'
    },
    {
      id: 5,
      title: 'Transportadores y Elevadores',
      description: 'Amplia gama de equipos modulares para el movimiento, elevación y descarga de granos, fabricados con materiales de alta resistencia y mantenimiento mínimo.'
    }
  ];

  const scrollToRepresentaciones = () => {
    const element = document.getElementById('representaciones-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="home-container">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <p className="hero-eyebrow">Excelencia aplicada a la ingeniería</p>
          <h1 className="hero-title">
            Más de 30 años impulsando el desarrollo industrial
          </h1>
          <Button 
            size="lg" 
            className="hero-cta"
            onClick={scrollToRepresentaciones}
          >
            <ArrowRight className="mr-2" size={20} />
            Ver Representaciones
          </Button>
        </div>
      </section>

      {/* Representaciones Section */}
      <section id="representaciones-section" className="representaciones-section">
        <div className="container">
          <h2 className="section-title">Nuestras Representaciones</h2>
          <p className="section-description">
            Representamos a <strong>marcas líderes</strong> internacionales en tecnología industrial, 
            ofreciendo soluciones confiables y eficientes para cada sector productivo.
          </p>
          
          <div className="representaciones-grid">
            {representaciones.map((rep) => (
              <Card key={rep.id} className="representacion-card" onClick={() => navigate(rep.link)}>
                <CardContent className="card-content">
                  <div className="card-logo-container">
                    <img src={rep.logo} alt={rep.title} className="card-logo" />
                  </div>
                  <h3 className="card-title">{rep.title}</h3>
                  <p className="card-description">{rep.description}</p>
                </CardContent>
                <span className="rep-card-arrow">→</span>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Productos KSB Section */}
      <section className="productos-ksb-section">
        <div className="container">
          <p className="section-eyebrow">Productos de KSB</p>
          
          <div className="productos-grid">
            {productosKSB.map((producto) => (
              <div 
                key={producto.id} 
                className="producto-item producto-item-clickable"
                onClick={() => producto.isPdf ? window.open(producto.link, '_blank') : navigate(producto.link)}
                data-testid={`producto-ksb-${producto.id}`}
              >
                <div className="producto-icon">
                  <CheckCircle2 size={24} />
                </div>
                <div className="producto-content">
                  <h3 className="producto-title">{producto.title}</h3>
                  <p className="producto-description">{producto.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="section-cta">
            <p className="section-cta-text">
              Soluciones confiables para infraestructura, energía e industria.
            </p>
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => navigate('/ksb')}
            >
              Ver todos los productos de KSB
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </div>
        </div>
      </section>

      {/* Equipos TECNAL Section */}
      <section className="equipos-tecnal-section">
        <div className="container">
          <p className="section-eyebrow">Equipos destacados de TECNAL</p>
          
          <div className="equipos-list">
            {equiposTecnal.map((equipo) => (
              <div key={equipo.id} className="equipo-item">
                <div className="equipo-number">{equipo.id}</div>
                <div className="equipo-content">
                  <h3 className="equipo-title">{equipo.title}</h3>
                  <p className="equipo-description">{equipo.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="section-cta">
            <p className="section-cta-text">
              Innovación y tecnología al servicio del desarrollo agroindustrial.
            </p>
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => navigate('/representaciones/tecnal')}
            >
              Ver todos los productos de TECNAL
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </div>
        </div>
      </section>

      {/* Sobre Nosotros Section */}
      <section className="sobre-nosotros-section">
        <div className="container">
          <h2 className="section-title">Sobre Nosotros</h2>
          <p className="section-subtitle">
            Desde 1991, acompañamos el crecimiento de la industria en Argentina.
          </p>
          <p className="section-text">
            Trabajamos como nexo entre los fabricantes líderes y las empresas industriales de la región, 
            ofreciendo equipos confiables y soporte especializado para plantas productivas de distintos sectores.
            <br /><br />
            Nuestra misión es acercar a las industrias locales la tecnología y calidad de marcas internacionales 
            consolidadas, contribuyendo a mejorar sus procesos y su eficiencia operativa.
          </p>
          <Button 
            size="lg" 
            className="section-cta-button"
            onClick={() => window.open(whatsappLink, '_blank')}
          >
            <Phone className="mr-2" size={20} />
            Contactanos
          </Button>
        </div>
      </section>

      {/* Clientes Section */}
      <section className="clientes-section">
        <div className="container">
          <h2 className="section-title">Nuestros Clientes</h2>
          <p className="section-text">
            <strong>Más de 200 empresas</strong> nos eligen por nuestra trayectoria, 
            compromiso y la calidad de nuestras soluciones.
          </p>
          <Button 
            variant="outline" 
            size="lg"
            onClick={() => navigate('/clientes')}
          >
            Ver todos nuestros clientes
            <ArrowRight className="ml-2" size={18} />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Home;